// Central date formatting for the whole app. Every user-facing date should go
// through `formatDate` so the style lives in ONE place and can be restyled later.
//
// Current style: US long form — "June 7, 2026". Locale-aware (English renders the
// US month-first form; other locales render their own long form), driven by the
// active i18n locale via `useI18n().formatDate`. To change the app-wide date
// style, change the Intl options in `formatDate` below — nothing else.

// Parse a backend date value WITHOUT a timezone shift. A bare "YYYY-MM-DD" is a
// calendar date with no zone: `new Date("2026-07-19")` would read it as UTC
// midnight and then render as the previous day in western zones. We build a local
// Date from the parts instead. Full ISO timestamps (with a time/zone) keep their
// instant.
export function toCalendarDate(value: Date | string): Date {
  if (value instanceof Date) return value
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (dateOnly) {
    const [, y, m, d] = dateOnly
    return new Date(Number(y), Number(m) - 1, Number(d))
  }
  return new Date(value)
}

// "June 7, 2026". Pass the active locale (see `useI18n`); defaults to English so
// non-hook callers still get the US long form.
export function formatDate(value: Date | string, locale = 'en'): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(toCalendarDate(value))
}
